import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import PriceChart from "@/components/PriceChart";
import OrderTicket from "@/components/OrderTicket";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Star } from "@phosphor-icons/react";
import { SymbolLogo } from "@/components/SymbolSearchInput";
import { toast } from "sonner";

const DEFAULT_SYMBOLS = ["AAPL", "MSFT", "NVDA", "TSLA", "GOOGL", "AMZN", "META"];

export default function TradePage() {
  const [sp, setSp] = useSearchParams();
  const [symbol, setSymbol] = useState((sp.get("s") || "AAPL").toUpperCase());
  const [quote, setQuote] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => { setSp({ s: symbol }); }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    loadQuote();
    const t = setInterval(loadQuote, 15000);
    return () => clearInterval(t);
  }, [symbol]);

  useEffect(() => { loadWatchlist(); }, []);

  async function loadQuote() {
    try {
      const { data } = await api.get(`/market/quote/${symbol}`);
      setQuote(data);
    } catch { setQuote(null); }
  }

  async function loadWatchlist() {
    try {
      const { data } = await api.get("/watchlist");
      setWatchlist(data.items || []);
    } catch {}
  }

  async function doSearch(q) {
    setSearch(q);
    if (!q) { setSearchResults([]); return; }
    try {
      const { data } = await api.get(`/market/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data.results || []);
    } catch { setSearchResults([]); }
  }

  async function addToWatchlist() {
    if (inWatchlist) return;
    try {
      await api.post("/watchlist", { symbol });
      toast.success(`${symbol} added to watchlist`);
      loadWatchlist();
    } catch (e) { toast.error("Failed to add"); }
  }

  // Keyboard shortcut — hitting "P" while on this page (and not typing in
  // any input) pins the currently-viewed symbol to the watchlist. Wider
  // surface for the mood-feed pin flow from Iter 40 so power users can
  // add symbols they're actively researching without reaching for a mouse.
  useEffect(() => {
    const handler = (e) => {
      if (e.key?.toLowerCase() !== "p") return;
      // Skip if the user is typing in an input, textarea, or contentEditable.
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return; // don't hijack Cmd+P print
      e.preventDefault();
      addToWatchlist();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, watchlist]);

  const up = (quote?.change ?? 0) >= 0;
  const inWatchlist = watchlist.find(w => w.symbol === symbol);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="trade-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Trade</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2">Trade</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Symbol search + quick list */}
        <Card className="lg:col-span-3">
          <div className="p-4 border-b border-zinc-800">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="search-input"
                placeholder="Search AAPL, MSFT…"
                value={search}
                onChange={(e) => doSearch(e.target.value.toUpperCase())}
                className="pl-8 bg-zinc-950 border-zinc-700 rounded-sm mono h-9 text-sm"
              />
            </div>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {(searchResults.length > 0 ? searchResults : DEFAULT_SYMBOLS.map(s => ({ symbol: s, name: s, price: null, logo: `https://financialmodelingprep.com/image-stock/${s}.png` }))).map((s) => (
              <button
                key={s.symbol}
                data-testid={`symbol-${s.symbol}`}
                onClick={() => setSymbol(s.symbol)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-zinc-800/50 border-b border-zinc-800/50 ${
                  symbol === s.symbol ? "bg-zinc-800/70 border-l-2 border-l-[#00C805]" : ""
                }`}
              >
                <SymbolLogo symbol={s.symbol} logo={s.logo} size={22} />
                <div className="flex-1 min-w-0">
                  <div className="mono text-sm font-semibold">{s.symbol}</div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[120px]">{s.name}</div>
                </div>
                {s.price != null && <div className="mono text-xs text-zinc-400">${s.price?.toFixed(2)}</div>}
              </button>
            ))}
          </div>
        </Card>

        {/* Center: Quote + chart */}
        <div className="lg:col-span-6 space-y-4">
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-baseline gap-3">
                  <h2 className="heading text-3xl font-black tracking-tighter mono">{symbol}</h2>
                  <button
                    onClick={addToWatchlist}
                    data-testid="add-watchlist-btn"
                    className="text-zinc-500 hover:text-yellow-400 transition-colors"
                    title={inWatchlist ? "Already on your watchlist" : "Add to watchlist · shortcut: P"}
                  >
                    <Star size={16} weight={inWatchlist ? "fill" : "regular"} className={inWatchlist ? "text-yellow-400" : ""} />
                  </button>
                  {!inWatchlist && (
                    <span
                      data-testid="pin-shortcut-hint"
                      title="Press P to pin this symbol to your watchlist"
                      className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-500 select-none"
                    >
                      Pin · <kbd className="text-white font-bold">P</kbd>
                    </span>
                  )}
                </div>
                <div className="text-sm text-zinc-500 mt-1">{quote?.name || symbol}</div>
              </div>
              <div className="text-right">
                <div className="mono text-4xl font-bold">{quote?.price ? fmtMoney(quote.price) : "—"}</div>
                <div className={`mono text-sm mt-1 ${up ? "profit" : "loss"}`}>
                  {quote ? `${up ? "+" : ""}${quote.change?.toFixed(2)} (${fmtPct(quote.change_pct)})` : ""}
                </div>
              </div>
            </div>
            {quote && (
              <div className="grid grid-cols-4 gap-2 mt-5 pt-4 border-t border-zinc-800">
                <div><div className="text-[10px] text-zinc-500 mono uppercase">Prev close</div><div className="mono text-sm mt-1">{fmtMoney(quote.prev_close)}</div></div>
                <div><div className="text-[10px] text-zinc-500 mono uppercase">Day high</div><div className="mono text-sm mt-1">{fmtMoney(quote.day_high)}</div></div>
                <div><div className="text-[10px] text-zinc-500 mono uppercase">Day low</div><div className="mono text-sm mt-1">{fmtMoney(quote.day_low)}</div></div>
                <div><div className="text-[10px] text-zinc-500 mono uppercase">Volume</div><div className="mono text-sm mt-1">{quote.volume?.toLocaleString() || "—"}</div></div>
              </div>
            )}
          </Card>
          <Card className="min-h-[400px]">
            <PriceChart symbol={symbol} height={360} />
          </Card>
        </div>

        {/* Right: Order ticket */}
        <Card className="lg:col-span-3">
          <OrderTicket symbol={symbol} quote={quote} onSubmitted={() => { loadQuote(); }} />
        </Card>
      </div>
    </div>
  );
}
